<template>
  <b-sidebar id="cta-sidebar" title="Add Custom Button" v-model="menuOpen" @shown="setPreview" right shadow no-header>
    <div class="px-3 py-2 mt-2">
      <h1 class="cta-title">
        Add Custom Button
        <pro-badge utm_campaign="upgrade" utm_source="viewer" utm_medium="heading"/>
      </h1>
      <p class="cta-description">
        Use a custom button to give your viewers an action they can take.
        For example a link to book a meeting, to a support article, or to a product you’re selling.
      </p>
    </div>

    <div class="px-3 py-2 mt-2">
      <rails-b-form ref="ctaForm">
        <input type="hidden" name="item_id" :value="item.id">
        <input type="hidden" name="call_to_action[slug]" :value="call_to_action.id">
        <div class="form-group">
          <input
            type="button"
            class="cloudapp-btn white btn-block custom-control-button"
            value="Use Last Settings"
            @click="useLastSettings"
            :disabled="!this.last_cta"
          />
        </div>

        <div class="form-group">
          <label class="cta-input-label">Button Text</label>
          <input type="text" class="cta-form-control" name="call_to_action[text]" v-model="form.text" data-testid="cta-button-text">
        </div>

        <div class="form-group">
          <label class="cta-input-label">Button Link (URL)</label>
          <input type="url" class="cta-form-control" name="call_to_action[link]" v-model="form.link" data-testid="cta-link" pattern="https?://.*|raw:.*" placeholder="https://example.com" required>
        </div>

        <label class="cta-input-label">Button color</label>
        <div class="form-group" ref="btnColorPickerGroup">
          <chrome-picker
            v-if="buttonColorPicker"
            :value="form.button_color"
            @input="changeButtonColor"
            data-testid="cta-button-color-picker"
            v-closable="{
              exclude: ['btnColorInput', 'btnColorPickerGroup'],
              handler: 'onCloseBtnColorPicker'
            }"
          />
          <input type="hidden" name="call_to_action[button_color]" :value="form.button_color">

          <color-input
            ref="btnColorInput"
            :value="form.button_color"
            @focus="togglePicker('buttonColorPicker', $event)"
            testid="cta-button-color">
          </color-input>
        </div>

        <label class="cta-input-label">Text color</label>
        <div class="form-group" ref="txtColorPickerGroup">
          <chrome-picker
            v-if="textColorPicker"
            :value="form.text_color"
            @input="changeTextColor"
            data-testid="cta-text-color-picker"
            v-closable="{
              exclude: ['txtColorInput', 'txtColorPickerGroup'],
              handler: 'onCloseTxtColorPicker'
            }"
          />

          <input type="hidden" name="call_to_action[text_color]" :value="form.text_color">
          <color-input
            ref="txtColorInput"
            :value="form.text_color"
            @focus="togglePicker('textColorPicker', $event)"
            testid="cta-text-color">
          </color-input>
        </div>

        <div class="form-group">
          <label class="cta-input-label">Corner style</label>
          <input type="hidden" name="call_to_action[corner_style]" :value="form.corner_style">
          <cta-dropup
            :initial_value="form.corner_style"
            @update:corner_style="form.corner_style = $event"
            icon :options="cornerOptions"
            field_name="corner_style"
            testid="cta-corner-style-dropdown"/>
        </div>

        <div class="form-group">
          <label class="cta-input-label">Appearance</label>
          <input type="hidden" name="call_to_action[appearance]" :value="form.appearance">
          <cta-dropup
            :initial_value="form.appearance"
            @update:appearance="form.appearance = $event"
            :options="appearanceOptions"
            field_name="appearance"
            testid="cta-appearance-dropdown"/>
        </div>

        <div class="form-check custom-control custom-switch">
          <b-form-checkbox switch name="call_to_action[display_action]" :checked-value="true" :unchecked-value="false" v-model="form.display_action" data-testid="cta-display-action">
            Display action
          </b-form-checkbox>
        </div>
      </rails-b-form>
    </div>

    <div class="px-3 py-2 mt-2">
      <div class="form-group" v-if="!currentOrganization.attributes.free">
        <b-button @click="submitForm()" class="mt-2" variant="primary" block data-testid="cta-submit">Save Button</b-button>
        <b-button variant="outline-secondary" block @click="cancelForm()">Cancel</b-button>
      </div>
      <div class="form-group" v-else>
        <a href="/plans?utm_source=viewer&amp;utm_campaign=upgrade&amp;utm_medium=cta_button" class="cloudapp-btn pro btn-block cta-upgrade-button" data-testid="cta-upgrade-button">
          Upgrade to Unlock
        </a>
      </div>
    </div>
  </b-sidebar>
</template>

<script>
import _ from "lodash";
import RailsBForm from "@/src/components/rails_b_form.vue";
import CtaDropup from "@/src/components/viewer/cta/cta_dropup.vue";
import ColorInput from '@/src/components/color_input.vue';
import ProBadge from '@/src/components/pro_badge.vue';
import { Chrome } from 'vue-color';
import { mapState } from 'vuex';

export default {
  components: { ChromePicker: Chrome, RailsBForm, CtaDropup, ProBadge, ColorInput },
  template: "#cta-menu",
  name: "cta-menu",
  props: {
    last_cta: {},
    cta: { type: Object, required: true }
  },
  computed: {
    ...mapState(['item', 'call_to_action', 'currentOrganization']),
    changeData: function() {
      const { slug, text, link, corner_style, button_color, text_color, appearance, display_action } = this.form;
      return {
        slug, text, link, corner_style, button_color, text_color, appearance, display_action: !!display_action
      }
    },
    formCompleted: function() {
      const formFields = ["link", "text", "corner_style", "text_color", "button_color", "appearance"];
      return formFields.every(function(prop) {
        return this[prop] !== null && this[prop] !== undefined && this[prop] !== "";
      }.bind(this));
    },
    appearanceOptions: function(){
      const hoverText = this.item.attributes.item_type == "video" ? 'When video ends' : 'On hover';
      return [
        {text: "Below the content", value: "cta-button-below", testid: "cta-appearance-below"},
        {text: hoverText, value: "cta-button-hover", testid: "cta-appearance-hover"}
      ]
    }
  },
  watch: {
    changeData: {
      deep: true,
      handler: function(value) {
        const { slug, text, link, corner_style, button_color, text_color, appearance, display_action } = value;
        this.$emit("update:cta", { slug, text, link, corner_style, button_color, text_color, appearance, display_action });
      }
    },
    menuOpen: function(val) {
      this.$emit("update:menu-open", val);
      if (val) {
        this.$root.$refs.viewerSidebarPanels.hide();
      } else {
        this.$root.$refs.viewerSidebarPanels.show();
      }
    }
  },
  methods: {
    setPreview() {
      this.form = _.clone(this.cta);
      this.$emit("update:cta", this.changeData);
    },
    useLastSettings: function() {
      for (const [k, v] of Object.entries(this.last_cta)) {
        this.$set(this.form, k, v);
      }
    },
    toggleSidebar: function() {
      this.menuOpen = !this.menuOpen;
    },
    dropupUpdateField: function(name, value) {
      this.form[name] = value;
    },
    changeButtonColor: function(colors) {
      this.form.button_color = colors.hex;
    },
    changeTextColor: function(colors) {
      this.form.text_color = colors.hex;
    },
    togglePicker: function(picker, value) {
      const fields = ["buttonColorPicker", "textColorPicker"];
      this[picker] = value;

      if (value) {
        this[fields[(fields.indexOf(picker) + 1) % 2]] = false;
      }
    },
    onCloseBtnColorPicker: function(e) {
      this["buttonColorPicker"] = false;
    },
    onCloseTxtColorPicker: function(e) {
      this["textColorPicker"] = false;
    },
    submitForm: function() {
      const vm = this;
      const validForm = vm.$refs.ctaForm.$refs.real_form.reportValidity()
      if(!validForm){ return; }

      this.$root.commitPreview();
      vm.$refs.ctaForm.submit(vm.$root.routes.api_v5_call_to_actions_path(), { "call_to_action[display_action]": !!vm.form.display_action }).
        then(function(resp) {
          vm.$emit("update:cta_id", resp.data.data.call_to_action.id);
          vm.toggleSidebar();
          vm.$root.toastSuccess('Button saved and applied');
        }).
        catch(function(error) {
          vm.$root.toastError(`Could not create/update button: ${error}`);
        });
    },
    cancelForm: function() {
      this.$root.clearPreview();
      this.toggleSidebar();
    }
  },
  data: function() {
    return {
      menuOpen: false,
      form: this.cta,
      cornerOptions: [
        {text: "Square", value: "cta-square", icon_class: "cta-square-icon", testid: "cta-corner-style-square"},
        {text: "Slightly Rounded", value: "cta-slightly-rounded", icon_class: "cta-slightly-rounded-icon", testid: "cta-corner-style-slightly-rounded"},
        {text: "Rounded", value: "cta-rounded", icon_class: "cta-rounded-icon", testid: "cta-corner-style-rounded"}
      ],
      buttonColorPicker: false,
      textColorPicker: false
    }
  }
}
</script>

<style lang="scss" scoped>
@import "application.scss";

.cta-title {
  height: 29px;
  font-family: 'SFpro';
  font-size: 24px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: $black;
}
.cta-description {
  font-family: 'SFpro';
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: $warm-gray;
}
.cta-input-label {
  height: 13px;
  font-family: 'SFpro';
  font-size: 11px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: $warm-gray;
}

.cta-form-control {
  border: 1px solid #e9ecef !important;
  text-shadow: none;
  box-shadow: none;
  background-color: $white;
  width: 100%;
  height: $cta-form-control-height;
  border: none;
  padding: 0 15px;
  border-radius: 5px;
  font-weight: 300;
  font-size: 16px;
  -webkit-transition: .4s;
  transition: .4s;
}
.cta-form-control:focus {
  box-shadow: unset;
  padding-left: 20px;
}
.cta-submit-button:disabled,
.custom-control-button:disabled {
  opacity: .3;
  cursor: not-allowed;
}
.custom-control-button {
  &:focus {
    outline-style: none;
  }
}
.vc-chrome {
  position: absolute;
  z-index: 2;
}
</style>
